import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { trackPageView } from "@/lib/analytics";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import NewReviewPage from "@/pages/NewReviewPage";
import ResultsPage from "@/pages/ResultsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PricingPage from "@/pages/PricingPage";
import SettingsPage from "@/pages/SettingsPage";
import BrandingPage from "@/pages/BrandingPage";
import AccountPage from "@/pages/AccountPage";
import UsersPage from "@/pages/UsersPage";
import OwnerDashboardPage from "@/pages/OwnerDashboardPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentPages";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

function OnboardedGate({ children }) {
  const { company } = useAuth();
  if (company && !company.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

const app = (Page) => (
  <ProtectedRoute>
    <OnboardedGate>
      <AppLayout>
        <Page />
      </AppLayout>
    </OnboardedGate>
  </ProtectedRoute>
);

function RouteAnalytics() {
  const location = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    // Guard against duplicate page_views (e.g. StrictMode double-invoke or repeat renders)
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteAnalytics />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/" element={app(DashboardPage)} />
          <Route path="/new" element={app(NewReviewPage)} />
          <Route path="/review/:id" element={app(ResultsPage)} />
          <Route path="/analytics" element={app(AnalyticsPage)} />
          <Route path="/pricing" element={app(PricingPage)} />
          <Route path="/settings" element={app(SettingsPage)} />
          <Route path="/branding" element={app(BrandingPage)} />
          <Route path="/account" element={app(AccountPage)} />
          <Route path="/users" element={app(UsersPage)} />
          <Route path="/owner" element={app(OwnerDashboardPage)} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
